// AppServer404LogsAdmin.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

/** ----------------------------------------------------------------
 * AppServer404LogsAdmin
 *
 * Purpose:
 *   Admin UI to view 404 retry/appserver logs captured by the proxy Lambda.
 *
 * Backend:
 *   - GET {backEndUrl}/appserver-404/logs?limit=200&nextKey=<json>&all=true
 *     → { items: [...], count, nextKey? }
 *
 * Notes:
 *   - If your backend uses scan() the returned order is not guaranteed.
 *     This UI supports client-side sorting and optional “Load all then sort”.
 *
 * Optional role gate (Learn):
 *   - GET /learn/api/public/v1/users/me  (institutionRoleIds)
 * ---------------------------------------------------------------- */

/** ---------------- i18n ---------------- */
const MESSAGES = {
    en_US: {
        gate: {
            checking: "Checking permissions…",
            blockedTitle: "Access Restricted",
            blockedMsg: "The App Server Logs tool is not available for your role.",
        },
        headings: {
            main: "App Server 404 Logs",
            controls: "Controls",
            table: "Log Entries",
            error: "Error",
        },
        buttons: {
            refresh: "Refresh",
            loadMore: "Load more",
            loadAll: "Load all",
            clear: "Clear",
            copy: "Copy",
        },
        labels: {
            tenant: "Tenant",
            role: "Role",
            pageSize: "Page size",
            sortBy: "Sort by",
            direction: "Direction",
            filter: "Filter (appserver / url / path)",
            only404: "Only 404s",
            showRaw: "Show raw JSON",
        },
        sort: {
            createdAt: "Time",
            appserver: "Appserver",
            statusCode: "Status",
            method: "Method",
            path: "Path",
            attempt: "Attempt",
        },
        direction: {
            desc: "Desc",
            asc: "Asc",
        },
        notices: {
            loading: "Loading logs…",
            loaded: "Logs loaded.",
            failedLoad: "Failed to load logs.",
            copied: "Copied to clipboard.",
            empty: "No entries found.",
        },
        // IMPORTANT: set this to whatever institution role you will use for gating
        // If you don’t want gating, set ROLE_GATING_ENABLED to "false" below.
        roleId: "APP_SERVER_LOGS_ADMIN",
    },
    en_AU: {},
};

const getMessages = (loc) => ({ ...MESSAGES.en_US, ...(MESSAGES[loc] || {}) });

/** ---------- Learn API adapter (role gate only) ---------- */
function makeLearnApi(platformUrl, token) {
    const base = (platformUrl || "").replace(/\/+$/, "");
    const http = axios.create({
        baseURL: base,
        timeout: 25000,
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    });

    const v1 = "/learn/api/public/v1";

    async function getCurrentUserInstitutionRoles() {
        const { data } = await http.get(`${v1}/users/me`);
        return data?.institutionRoleIds || [];
    }

    return { getCurrentUserInstitutionRoles };
}

/** ---------- Backend API adapter ---------- */
function makeBackendApi(backEndUrl, tokenOptional) {
    const base = (backEndUrl || "").replace(/\/+$/, "");
    const headers = { Accept: "application/json" };
    if (tokenOptional) headers.Authorization = `Bearer ${tokenOptional}`;

    const http = axios.create({
        baseURL: base,
        timeout: 25000,
        headers,
    });

    return {
        getLogsPage: async ({ limit = 200, nextKey = null }) => {
            const params = {};
            if (limit) params.limit = String(limit);
            if (nextKey) params.nextKey = JSON.stringify(nextKey);
            const { data } = await http.get(`/appserver-404/logs`, { params });
            return data || {};
        },
        getAllLogs: async () => {
            // if your backend supports ?all=true in the handler, use it
            const { data } = await http.get(`/appserver-404/logs`, { params: { all: "true" } });
            return data || {};
        },
    };
}

/** ---------- Normalization ---------- */
function normalizeLogItem(it) {
    // supports minor casing differences
    const createdAt = String(it?.createdAt ?? it?.CreatedAt ?? it?.timestamp ?? it?.Timestamp ?? "");
    const statusCode = Number(it?.statusCode ?? it?.StatusCode ?? 0) || 0;
    const method = String(it?.method ?? it?.Method ?? "").toUpperCase();
    const path = String(it?.path ?? it?.Path ?? "");
    const query = String(it?.query ?? it?.Query ?? "");
    const targetUrl = String(it?.targetUrl ?? it?.TargetUrl ?? "");
    const appserver = String(it?.appserver ?? it?.Appserver ?? it?.appServer ?? it?.AppServer ?? "");
    const attempt = Number(it?.attempt ?? it?.Attempt ?? 0) || 0;
    const sourceIp = String(it?.sourceIp ?? it?.SourceIp ?? it?.sourceIP ?? "");
    const userAgent = String(it?.userAgent ?? it?.UserAgent ?? "");

    // keep original keys too (useful for raw)
    return {
        _raw: it,
        createdAt,
        statusCode,
        method,
        path,
        query,
        targetUrl,
        appserver,
        attempt,
        sourceIp,
        userAgent,
    };
}

function safeJson(v) {
    try {
        return JSON.stringify(v, null, 2);
    } catch {
        return String(v);
    }
}

function compareValues(a, b) {
    // handles numbers and strings
    if (typeof a === "number" && typeof b === "number") return a - b;
    const as = String(a ?? "");
    const bs = String(b ?? "");
    return as.localeCompare(bs);
}

function sortLogs(items, sortKey, dir) {
    const direction = dir === "asc" ? 1 : -1;
    const key = sortKey || "createdAt";

    // createdAt is ISO in your logger -> lexicographically sortable if ISO-8601
    const getVal = (x) => {
        if (key === "createdAt") return String(x.createdAt || "");
        if (key === "statusCode") return Number(x.statusCode || 0);
        if (key === "attempt") return Number(x.attempt || 0);
        if (key === "appserver") return String(x.appserver || "");
        if (key === "method") return String(x.method || "");
        if (key === "path") return String(x.path || "");
        return String(x[key] ?? "");
    };

    return [...items].sort((x, y) => direction * compareValues(getVal(x), getVal(y)));
}

/** ---------- Component ---------- */
const AppServer404LogsAdmin = ({
                                   platformUrl,
                                   backEndUrl,
                                   locale = "en_US",
                                   tenantId, // unused but displayed to match your style
                                   roleGatingEnabled = true, // set false if you don't want the Learn role check
                               }) => {
    const M = useMemo(() => getMessages(locale), [locale]);

    const location = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    // Optional token in URL (common in your other tools)
    const token = queryParams.get("token") || "";

    const learn = useMemo(() => makeLearnApi(platformUrl, token), [platformUrl, token]);

    // For the logs endpoint you may not need auth at all; we pass it only if present.
    const backend = useMemo(() => makeBackendApi(backEndUrl, token || ""), [backEndUrl, token]);

    // Gate
    const [roleChecked, setRoleChecked] = useState(false);
    const [hasAdminRole, setHasAdminRole] = useState(false);

    // Data
    const [loading, setLoading] = useState(true);
    const [fatalError, setFatalError] = useState("");
    const [inlineError, setInlineError] = useState("");
    const [inlineSuccess, setInlineSuccess] = useState("");

    const [items, setItems] = useState([]);
    const [nextKey, setNextKey] = useState(null);

    // Controls
    const [pageSize, setPageSize] = useState(200);
    const [sortBy, setSortBy] = useState("createdAt");
    const [direction, setDirection] = useState("desc");
    const [filterText, setFilterText] = useState("");
    const [only404, setOnly404] = useState(true);
    const [showRaw, setShowRaw] = useState(false);

    // Prevent overlapping loads
    const inFlight = useRef(false);

    const compactCss = `
    .bb-container { font-size: 13px; }
    .bb-card h2 { font-size: 16px; font-weight: 700 !important; margin: 0; }
    .bb-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }

    .bb-card .bb-button,
    .bb-container .bb-button {
      -webkit-appearance: none !important;
      appearance: none !important;
      background: #f6f6f6 !important;
      border: 1px solid #d3d3d3 !important;
      color: #222 !important;
      border-radius: 6px !important;
      padding: 6px 10px !important;
      font: inherit !important;
      line-height: 1.2 !important;
      cursor: pointer !important;
    }
    .bb-card .bb-button:hover,
    .bb-container .bb-button:hover { background: #efefef !important; }
    .bb-card .bb-button:focus-visible { outline: 2px solid #5a9cff !important; outline-offset: 2px !important; }

    .bb-card .bb-button.primary { background: #2c6bed !important; border-color: #2c6bed !important; color: #fff !important; }
    .bb-card .bb-button.primary:hover { background: #255dd1 !important; border-color: #255dd1 !important; }

    .bb-card .bb-button.secondary { background: #f2f2f2 !important; border-color: #d0d0d0 !important; color: #222 !important; }

    textarea, input, select { font-size: 12px; padding: 8px 10px; font-weight: 400; color: inherit; border-radius: 6px; border: 1px solid #ddd; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border-bottom: 1px solid #eee; padding: 8px 8px; vertical-align: top; font-size: 12px; }
    th { text-align: left; background: #fafafa; position: sticky; top: 0; z-index: 1; }
    code { font-size: 11px; }
  `;

    const Flash = ({ type, children }) => {
        if (!children) return null;
        const styles = {
            marginBottom: 6,
            padding: "8px 10px",
            borderRadius: 6,
            border: "1px solid",
            background: type === "error" ? "#ffecec" : "#ecfff1",
            borderColor: type === "error" ? "#ffb3b3" : "#b3ffcc",
            color: type === "error" ? "#a40000" : "#005c2e",
            fontSize: 12,
            whiteSpace: "pre-wrap",
        };
        return (
            <div role={type === "error" ? "alert" : "status"} style={styles} aria-live="polite">
                {children}
            </div>
        );
    };

    /** ---------------- Role gate ---------------- */
    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (!roleGatingEnabled) {
                if (!cancelled) {
                    setHasAdminRole(true);
                    setRoleChecked(true);
                }
                return;
            }

            try {
                if (!platformUrl || !token) {
                    if (!cancelled) {
                        setHasAdminRole(false);
                        setRoleChecked(true);
                    }
                    return;
                }

                const roles = await learn.getCurrentUserInstitutionRoles();
                if (!cancelled) {
                    setHasAdminRole(Array.isArray(roles) && roles.includes(M.roleId));
                    setRoleChecked(true);
                }
            } catch {
                if (!cancelled) {
                    setHasAdminRole(false);
                    setRoleChecked(true);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [learn, platformUrl, token, M.roleId, roleGatingEnabled]);

    /** ---------------- Loaders ---------------- */
    async function refreshFirstPage() {
        if (inFlight.current) return;
        inFlight.current = true;

        setLoading(true);
        setFatalError("");
        setInlineError("");
        setInlineSuccess("");

        try {
            const data = await backend.getLogsPage({ limit: pageSize, nextKey: null });
            const rawItems = Array.isArray(data?.items) ? data.items : [];
            setItems(rawItems.map(normalizeLogItem));
            setNextKey(data?.nextKey || null);
            setInlineSuccess(M.notices.loaded);
        } catch (err) {
            setFatalError(err?.response?.data?.message || err.message || M.notices.failedLoad);
        } finally {
            setLoading(false);
            inFlight.current = false;
        }
    }

    async function loadMore() {
        if (inFlight.current) return;
        if (!nextKey) return;

        inFlight.current = true;
        setInlineError("");
        setInlineSuccess("");

        try {
            const data = await backend.getLogsPage({ limit: pageSize, nextKey });
            const rawItems = Array.isArray(data?.items) ? data.items : [];
            setItems((prev) => [...prev, ...rawItems.map(normalizeLogItem)]);
            setNextKey(data?.nextKey || null);
        } catch (err) {
            setInlineError(err?.response?.data?.message || err.message || "Failed to load next page.");
        } finally {
            inFlight.current = false;
        }
    }

    async function loadAll() {
        if (inFlight.current) return;
        inFlight.current = true;

        setInlineError("");
        setInlineSuccess("");
        setLoading(true);

        try {
            // Prefer server all=true if supported; otherwise page through.
            let data;
            try {
                data = await backend.getAllLogs();
            } catch {
                data = null;
            }

            if (data && Array.isArray(data?.items)) {
                setItems(data.items.map(normalizeLogItem));
                setNextKey(null);
                setInlineSuccess(M.notices.loaded);
                return;
            }

            // Fallback: page through until nextKey is null
            let agg = [];
            let nk = null;

            const first = await backend.getLogsPage({ limit: pageSize, nextKey: null });
            agg = agg.concat((first?.items || []).map(normalizeLogItem));
            nk = first?.nextKey || null;

            while (nk) {
                const page = await backend.getLogsPage({ limit: pageSize, nextKey: nk });
                agg = agg.concat((page?.items || []).map(normalizeLogItem));
                nk = page?.nextKey || null;
            }

            setItems(agg);
            setNextKey(null);
            setInlineSuccess(M.notices.loaded);
        } catch (err) {
            setInlineError(err?.response?.data?.message || err.message || "Failed to load all logs.");
        } finally {
            setLoading(false);
            inFlight.current = false;
        }
    }

    // Initial load once gated
    useEffect(() => {
        if (!(roleChecked && hasAdminRole)) return;
        refreshFirstPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleChecked, hasAdminRole, backEndUrl]);

    /** ---------------- Derived view ---------------- */
    const filteredSorted = useMemo(() => {
        const ft = String(filterText || "").trim().toLowerCase();

        let out = items;

        if (only404) out = out.filter((x) => Number(x.statusCode) === 404);

        if (ft) {
            out = out.filter((x) => {
                const hay = [
                    x.appserver,
                    x.targetUrl,
                    x.path,
                    x.method,
                    x.query,
                    x.sourceIp,
                    x.userAgent,
                ]
                    .join(" | ")
                    .toLowerCase();
                return hay.includes(ft);
            });
        }

        out = sortLogs(out, sortBy, direction);
        return out;
    }, [items, filterText, only404, sortBy, direction]);

    /** ---------------- UI helpers ---------------- */
    async function copyText(t) {
        setInlineError("");
        setInlineSuccess("");
        try {
            await navigator.clipboard.writeText(String(t || ""));
            setInlineSuccess(M.notices.copied);
        } catch {
            setInlineError("Could not copy to clipboard.");
        }
    }

    function clearLocal() {
        setItems([]);
        setNextKey(null);
        setInlineError("");
        setInlineSuccess("");
    }

    /** ---------------- Gates ---------------- */
    if (!roleChecked) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div>{M.gate.checking}</div>
            </div>
        );
    }

    if (!hasAdminRole) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                    <h2>{M.gate.blockedTitle}</h2>
                    <p style={{ fontSize: 12 }}>{M.gate.blockedMsg}</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div>{M.notices.loading}</div>
            </div>
        );
    }

    if (fatalError) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                    <h2 style={{ margin: 0 }}>{M.headings.error}</h2>
                    <p style={{ fontSize: 12 }}>{fatalError}</p>
                </div>
            </div>
        );
    }

    /** ---------------- Main UI ---------------- */
    return (
        <div className="bb-container" style={{ display: "grid", gap: 12 }}>
            <style dangerouslySetInnerHTML={{ __html: compactCss }} />

            {/* Header */}
            <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                <div className="bb-row" style={{ justifyContent: "space-between" }}>
                    <h2>{M.headings.main}</h2>
                    <div className="bb-row">
                        <button type="button" className="bb-button secondary" onClick={refreshFirstPage}>
                            {M.buttons.refresh}
                        </button>
                        <button type="button" className="bb-button secondary" onClick={clearLocal} title={M.buttons.clear}>
                            {M.buttons.clear}
                        </button>
                    </div>
                </div>

                <div style={{ fontSize: 12, color: "#555", marginTop: 8 }}>
                    <div>
                        Tenant: <b>{tenantId || "(none)"}</b>
                        {" "}• Loaded: <b>{items.length}</b>
                        {" "}• Showing: <b>{filteredSorted.length}</b>
                        {nextKey ? (
                            <>
                                {" "}• Next page: <b>Yes</b>
                            </>
                        ) : (
                            <>
                                {" "}• Next page: <b>No</b>
                            </>
                        )}
                    </div>
                    <div style={{ marginTop: 4 }}>
                        Sort is applied client-side. Use <b>Load all</b> if you want a global sort across the entire table.
                    </div>
                </div>
            </div>

            {/* Controls */}
            <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                <div className="bb-row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
                    <h2>{M.headings.controls}</h2>
                    <div className="bb-row">
                        <button
                            type="button"
                            className="bb-button secondary"
                            onClick={loadMore}
                            disabled={!nextKey}
                            aria-disabled={!nextKey}
                            title={M.buttons.loadMore}
                        >
                            {M.buttons.loadMore}
                        </button>
                        <button type="button" className="bb-button" onClick={loadAll} title={M.buttons.loadAll}>
                            {M.buttons.loadAll}
                        </button>
                    </div>
                </div>

                <Flash type="error">{inlineError}</Flash>
                <Flash type="success">{inlineSuccess}</Flash>

                <div className="bb-row" style={{ gap: 10 }}>
                    <label style={{ display: "grid", gap: 4, fontSize: 12 }}>
                        <span style={{ fontWeight: 600 }}>{M.labels.pageSize}</span>
                        <select
                            value={String(pageSize)}
                            onChange={(e) => setPageSize(Math.max(1, Math.min(1000, Number(e.target.value) || 200)))}
                            style={{ minWidth: 120 }}
                        >
                            {[50, 100, 200, 500, 1000].map((n) => (
                                <option key={n} value={String(n)}>
                                    {n}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label style={{ display: "grid", gap: 4, fontSize: 12 }}>
                        <span style={{ fontWeight: 600 }}>{M.labels.sortBy}</span>
                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ minWidth: 160 }}>
                            <option value="createdAt">{M.sort.createdAt}</option>
                            <option value="appserver">{M.sort.appserver}</option>
                            <option value="statusCode">{M.sort.statusCode}</option>
                            <option value="method">{M.sort.method}</option>
                            <option value="path">{M.sort.path}</option>
                            <option value="attempt">{M.sort.attempt}</option>
                        </select>
                    </label>

                    <label style={{ display: "grid", gap: 4, fontSize: 12 }}>
                        <span style={{ fontWeight: 600 }}>{M.labels.direction}</span>
                        <select value={direction} onChange={(e) => setDirection(e.target.value)} style={{ minWidth: 110 }}>
                            <option value="desc">{M.direction.desc}</option>
                            <option value="asc">{M.direction.asc}</option>
                        </select>
                    </label>

                    <label style={{ display: "grid", gap: 4, fontSize: 12, flex: 1, minWidth: 260 }}>
                        <span style={{ fontWeight: 600 }}>{M.labels.filter}</span>
                        <input
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            placeholder="e.g. appserver01 or /learn/api/v1/cc/query"
                        />
                    </label>

                    <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, marginTop: 18 }}>
                        <input type="checkbox" checked={only404} onChange={(e) => setOnly404(Boolean(e.target.checked))} />
                        <span>{M.labels.only404}</span>
                    </label>

                    <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, marginTop: 18 }}>
                        <input type="checkbox" checked={showRaw} onChange={(e) => setShowRaw(Boolean(e.target.checked))} />
                        <span>{M.labels.showRaw}</span>
                    </label>
                </div>
            </div>

            {/* Table */}
            <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                <div className="bb-row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
                    <h2>{M.headings.table}</h2>
                    <div style={{ fontSize: 12, color: "#555" }}>
                        Rows: <b>{filteredSorted.length}</b>
                    </div>
                </div>

                {filteredSorted.length === 0 ? (
                    <div style={{ fontSize: 12, color: "#555" }}>{M.notices.empty}</div>
                ) : (
                    <div style={{ overflow: "auto", border: "1px solid #eee", borderRadius: 6 }}>
                        <table>
                            <thead>
                            <tr>
                                <th style={{ minWidth: 160 }}>Time</th>
                                <th style={{ minWidth: 60 }}>Status</th>
                                <th style={{ minWidth: 70 }}>Attempt</th>
                                <th style={{ minWidth: 70 }}>Method</th>
                                <th style={{ minWidth: 220 }}>Path</th>
                                <th style={{ minWidth: 200 }}>Appserver</th>
                                <th style={{ minWidth: 360 }}>Target URL</th>
                                <th style={{ minWidth: 160 }}>Source</th>
                                <th style={{ minWidth: 120 }}>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredSorted.map((r, idx) => {
                                const time = r.createdAt ? r.createdAt.replace("T", " ").slice(0, 19) : "";
                                const pathWithQuery = r.query ? `${r.path}?${r.query}` : r.path;

                                return (
                                    <tr key={`${r.createdAt || "row"}#${idx}`}>
                                        <td>
                                            <div style={{ fontWeight: 700 }}>{time || "(unknown)"}</div>
                                        </td>

                                        <td>
                                            <div style={{ fontWeight: 700 }}>{r.statusCode || ""}</div>
                                        </td>

                                        <td>{r.attempt || ""}</td>

                                        <td>{r.method || ""}</td>

                                        <td>
                                            <div style={{ wordBreak: "break-word" }}>
                                                <code>{pathWithQuery || ""}</code>
                                            </div>
                                        </td>

                                        <td>
                                            <div style={{ wordBreak: "break-word" }}>
                                                <code>{r.appserver || "(missing)"}</code>
                                            </div>
                                        </td>

                                        <td>
                                            <div style={{ wordBreak: "break-word" }}>
                                                {r.targetUrl ? (
                                                    <a href={r.targetUrl} target="_blank" rel="noreferrer">
                                                        {r.targetUrl}
                                                    </a>
                                                ) : (
                                                    ""
                                                )}
                                            </div>
                                            {showRaw ? (
                                                <pre style={{ marginTop: 8, fontSize: 11, background: "#fafafa", border: "1px solid #eee", borderRadius: 6, padding: 8, whiteSpace: "pre-wrap" }}>
                            {safeJson(r._raw)}
                          </pre>
                                            ) : null}
                                        </td>

                                        <td>
                                            <div style={{ fontSize: 11, color: "#555" }}>
                                                {r.sourceIp ? (
                                                    <>
                                                        IP: <b>{r.sourceIp}</b>
                                                        <br />
                                                    </>
                                                ) : null}
                                                {r.userAgent ? (
                                                    <>
                                                        UA: <span title={r.userAgent}>{String(r.userAgent).slice(0, 24)}{String(r.userAgent).length > 24 ? "…" : ""}</span>
                                                    </>
                                                ) : null}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="bb-row" style={{ gap: 6 }}>
                                                <button type="button" className="bb-button secondary" onClick={() => copyText(r.targetUrl || "")} disabled={!r.targetUrl}>
                                                    {M.buttons.copy}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                )}

                {nextKey ? (
                    <div className="bb-row" style={{ justifyContent: "space-between", marginTop: 10 }}>
                        <div style={{ fontSize: 12, color: "#555" }}>
                            More pages available.
                        </div>
                        <div className="bb-row">
                            <button type="button" className="bb-button secondary" onClick={loadMore}>
                                {M.buttons.loadMore}
                            </button>
                            <button type="button" className="bb-button" onClick={loadAll}>
                                {M.buttons.loadAll}
                            </button>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default AppServer404LogsAdmin;